export const mode = process.env.NODE_ENV === "development" ? "test" : "index";

export const appFolder = "/";

const href = window.location.href;

export const host = window.location.host;

export const siteUrl = href.substring(
  href.indexOf(host) + host.length,
  href.indexOf(appFolder)
);

export const urlDefault = "";
export const urlImages = siteUrl + appFolder + "/assets/images/";
export const urlPreview = `${siteUrl}/_layouts/15/WopiFrame.aspx?sourcedoc=`;

export const urlLogOut = `${siteUrl}/_layouts/15/SignOut.aspx`;

export const changeTimeZone = (date: Date, timeZone: string) => {
  if (typeof date === "string") {
    return new Date(
      new Date(date).toLocaleString("en-US", {
        timeZone,
      })
    );
  }

  return new Date(
    date.toLocaleString("en-US", {
      timeZone,
    })
  );
};

export const getRandomColor = () => {
  // Генерируем случайные значения для R, G и B в диапазоне 50-200,
  // чтобы избежать слишком тёмных (0-50) и слишком светлых (200-255) цветов
  const r = Math.floor(Math.random() * 150) + 50; // 50-200
  const g = Math.floor(Math.random() * 150) + 50; // 50-200
  const b = Math.floor(Math.random() * 150) + 50; // 50-200

  // Преобразуем значения в шестнадцатеричный формат и добавляем ведущий ноль при необходимости
  const hexR = r.toString(16).padStart(2, "0");
  const hexG = g.toString(16).padStart(2, "0");
  const hexB = b.toString(16).padStart(2, "0");

  return `#${hexR}${hexG}${hexB}`.toUpperCase();
};

interface EmailAddress {
  name: string;
  email: string;
}

interface Attachment {
  name: string;
  content: Blob | File | ArrayBuffer | Uint8Array;
  mimeType?: string;
}

interface EmailOptions {
  from: EmailAddress;
  to: EmailAddress[];
  cc?: EmailAddress[];
  bcc?: EmailAddress[];
  subject: string;
  htmlBody?: string;
  textBody?: string;
  attachments?: Attachment[];
  fileName?: string;
}

/**
 * Скачивает письмо в формате EML, который гарантированно открывается в Outlook
 */
export async function downloadOutlookEmailWithAttachments(
  options: EmailOptions
): Promise<void> {
  try {
    // Валидация параметров
    if (!options.from?.email) throw new Error("Sender not specified");
    // if (!options.to?.length) throw new Error("Recipients not specified");

    const {
      from,
      to = [],
      cc = [],
      bcc = [],
      subject = "",
      htmlBody = "",
      textBody = "",
      attachments = [],
      fileName = "message.eml",
    } = options;

    // 1. Формируем MIME-структуру
    const mimeParts: string[] = [
      `X-Unsent: 1`,
      `X-Draft: yes`,
      `From: ${formatEmailAddress(from)}`,
      `To: ${to.length ? to.map(formatEmailAddress).join(", ") : []}`,
      ...(cc.length ? [`Cc: ${cc.map(formatEmailAddress).join(", ")}`] : []),
      ...(bcc.length ? [`Bcc: ${bcc.map(formatEmailAddress).join(", ")}`] : []),
      `Subject: ${subject}`,
      `Date: ${new Date().toUTCString()}`,
      `MIME-Version: 1.0`,
      `Content-Type: multipart/mixed; boundary="MIXED_BOUNDARY"`,
      ``,
      `--MIXED_BOUNDARY`,
      `Content-Type: multipart/alternative; boundary="ALT_BOUNDARY"`,
      ``,
      `--ALT_BOUNDARY`,
      `Content-Type: text/plain; charset=utf-8`,
      `Content-Transfer-Encoding: base64`,
      ``,
      base64Encode(textBody || stripHTML(htmlBody)),
      ``,
      `--ALT_BOUNDARY`,
      `Content-Type: text/html; charset=utf-8`,
      `Content-Transfer-Encoding: base64`,
      ``,
      base64Encode(htmlBody),
      ``,
      `--ALT_BOUNDARY--`,
    ];

    // 2. Добавляем вложения (включая .pptx)
    for (const attachment of attachments) {
      try {
        const { name, content, mimeType } = await prepareAttachment(attachment);

        mimeParts.push(
          `--MIXED_BOUNDARY`,
          `Content-Type: ${mimeType}`,
          `Content-Disposition: attachment; filename="${encodeMimeHeader(
            name
          )}"`,
          `Content-Transfer-Encoding: base64`,
          ``,
          content,
          ``
        );
      } catch (error) {
        console.error("Attachment processing error:", error);
      }
    }

    mimeParts.push(`--MIXED_BOUNDARY--`);

    // 3. Создаем и скачиваем EML
    const emlContent = mimeParts.join("\r\n");
    const blob = new Blob([emlContent], { type: "message/rfc822" });
    downloadBlob(blob, fileName);
  } catch (error) {
    console.error("Email creation failed:", error);
    throw error;
  }
}

// Вспомогательные функции

function formatEmailAddress(addr: EmailAddress): string {
  return `"${encodeMimeHeader(addr.name)}" <${addr.email}>`;
}

function encodeMimeHeader(text: string): string {
  return text.replace(
    /[^\x20-\x7E]/g,
    (ch) => `=?UTF-8?B?${btoa(unescape(encodeURIComponent(ch)))}?=`
  );
}

function base64Encode(text: string): string {
  return btoa(unescape(encodeURIComponent(text || "")));
}

function stripHTML(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
}

async function prepareAttachment(attachment: Attachment): Promise<{
  name: string;
  content: string;
  mimeType: string;
}> {
  let buffer: ArrayBuffer;

  if (
    attachment.content instanceof Blob ||
    attachment.content instanceof File
  ) {
    buffer = await readBlobAsArrayBuffer(attachment.content);
  } else if (attachment.content instanceof ArrayBuffer) {
    buffer = attachment.content;
  } else {
    buffer = attachment.content.buffer as ArrayBuffer;
  }

  return {
    name: attachment.name,
    content: arrayBufferToBase64(buffer),
    mimeType: attachment.mimeType || "application/octet-stream",
  };
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function readBlobAsArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = reject;
    reader.readAsArrayBuffer(blob);
  });
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

export function formatNumberToTime(number: number) {
  // Преобразуем число в строку
  const str = number.toString();

  // Добавляем ведущие нули, если число меньше 4 цифр (например, 935 -> "0935")
  const paddedStr = str.padStart(4, "0");

  // Разделяем строку на часы и минуты
  const hours = paddedStr.substring(0, 2);
  const minutes = paddedStr.substring(2, 4);

  // Возвращаем результат в формате "чч:мм"
  return `${hours}:${minutes}`;
}

let paramsBuffer: string[] = [];
let timeoutId: ReturnType<typeof setTimeout> | null = null;

export const addParamsAndRequest = (
  params: string,
  fetchFn: any,
  delay = 300
): void => {
  paramsBuffer.push(params);

  if (timeoutId) clearTimeout(timeoutId);

  timeoutId = setTimeout(async () => {
    const currentParams = [...paramsBuffer];
    paramsBuffer = [];

    try {
      await fetchFn(currentParams);
    } catch (err) {
      console.error("Ошибка при выполнении общего запроса:", err);
    }
  }, delay);
};

export function sanitizeString(value: string): string {
  return value
    .replace(/[<>:"/\\|?*~#%&{}]/g, "_") // замена запрещённых символов
    .replace(/_+/g, "_") // схлопываем несколько _
    .replace(/^_+|_+$/g, ""); // убираем _ в начале и конце
}

export const truncateByWords = (text: string, limit = 250): string => {
  if (text.length <= limit) return text;

  const truncated = text.slice(0, limit);
  const lastSpace = truncated.lastIndexOf(" ");

  return truncated.slice(0, lastSpace) + "…";
};

export const containsAllIds = (arr, ids) => {
  return ids.every((id) => arr.some((item) => item.get_lookupId() === id));
};

export const autoCloseModal = 1000;

export const USE_POST_THRESHOLD = 2000;

export function transformUrlToNewRoute(fullOriginalUrl, newRoute) {
  try {
    const url = new URL(fullOriginalUrl);

    // Заменяем путь на новый
    const newPath = newRoute.startsWith('/') ? newRoute : `/${newRoute}`;
    const baseUrl = url.origin + newPath;

    const params = {};

    url.searchParams.forEach((value, key) => {
      if (key.endsWith('[]')) {
        // Обработка массивов (filters[Trigger][$in][])
        const cleanKey = key.slice(0, -2);
        if (!params[cleanKey]) {
          params[cleanKey] = [];
        }
        params[cleanKey].push(isNaN(value) ? value : Number(value));
      } else {
        // Обычные параметры + преобразование чисел
        params[key] = isNaN(value) ? value : Number(value);
      }
    });

    return { baseUrl, params };
  } catch (error) {
    console.error('Ошибка преобразования URL:', error);
    return {
      baseUrl: newRoute.startsWith('http') ? newRoute : `https://supply.napitkivmeste.ru${newRoute}`,
      params: {}
    };
  }
}
